import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";

interface NotificationPermissionModalProps {
  open: boolean;
  onEnable: () => void;
  onDismiss: () => void;
  loading?: boolean;
  isBlocked?: boolean;
}

export function NotificationPermissionModal({ open, onEnable, onDismiss, loading, isBlocked }: NotificationPermissionModalProps) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onDismiss}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(103,75,245,0.18)",
        },
      }}
    >
      <Box
        sx={{
          height: 5,
          background: isBlocked
            ? "linear-gradient(90deg, #ef4444 0%, #f87171 100%)"
            : "linear-gradient(90deg, #674BF5 0%, #a78bfa 100%)",
        }}
      />

      <DialogContent sx={{ textAlign: "center", px: 4, pt: 4, pb: 3.5 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            background: isBlocked
              ? "linear-gradient(135deg, #ef4444 0%, #f87171 100%)"
              : "linear-gradient(135deg, #674BF5 0%, #a78bfa 100%)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
            boxShadow: isBlocked ? "0 12px 32px rgba(239,68,68,0.35)" : "0 12px 32px rgba(103,75,245,0.35)",
          }}
        >
          {isBlocked ? (
            <NotificationsOffIcon sx={{ fontSize: 36, color: "#fff" }} />
          ) : (
            <NotificationsNoneIcon sx={{ fontSize: 36, color: "#fff" }} />
          )}
        </Box>

        <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "text.primary" }}>
          {isBlocked ? "Notifications Blocked" : "Stay Updated with Notifications"}
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary", lineHeight: 1.6 }}>
          {isBlocked
            ? "Browser notifications are blocked. To receive real-time alerts, please enable notifications in your browser site settings."
            : "Get instant alerts for project updates, payment notifications, approval requests, and critical alerts — even when the tab is in background."}
        </Typography>

        <Button
          variant="contained"
          fullWidth
          disabled={loading}
          onClick={isBlocked ? onDismiss : onEnable}
          sx={{
            py: 1.2,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            background: isBlocked
              ? "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)"
              : "linear-gradient(135deg, #674BF5 0%, #a78bfa 100%)",
            "&:hover": {
              background: isBlocked
                ? "linear-gradient(135deg, #4b5563 0%, #6b7280 100%)"
                : "linear-gradient(135deg, #5a3fdb 0%, #9678e8 100%)",
            },
          }}
        >
          {isBlocked ? "Got it" : loading ? "Enabling..." : "Enable Notifications"}
        </Button>

        {!isBlocked && (
          <Button
            fullWidth
            disabled={loading}
            onClick={onDismiss}
            sx={{
              mt: 1,
              py: 1.2,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              color: "text.secondary",
            }}
          >
            Maybe Later
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
